/**
 * Scroll Behavior Detection Strategy
 * Autonomous module managing scroll event listeners
 * Tracks normalized scroll distance and velocity patterns
 */
import { BaseStrategy } from '../strategy.js';
import { calculateStatistics, gaussian, inverseSigmoid } from '../math-utils.js';
export class ScrollStrategy extends BaseStrategy {
    constructor(options) {
        super();
        this.name = 'scroll';
        this.defaultWeight = 0.15;
        this.distanceSeries = [];
        this.velocitySeries = [];
        this.lastScrollY = null;
        this.lastTimestamp = 0;
        this.rollingWindowMs = 30000;
        this.documentHeight = 1;
        this.listener = null;
        this.isActive = false;
        if ((options === null || options === void 0 ? void 0 : options.rollingWindow) !== undefined)
            this.rollingWindowMs = options.rollingWindow;
    }
    start() {
        if (this.isActive)
            return;
        this.isActive = true;
        // Calculate document height for normalization
        this.documentHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, 1);
        this.listener = (_e) => {
            const scrollY = window.scrollY;
            const now = Date.now();
            // Always update last position on first event
            if (this.lastScrollY === null) {
                this.lastScrollY = scrollY;
                this.lastTimestamp = now;
                return;
            }
            const pixelDelta = scrollY - this.lastScrollY;
            const deltaTime = now - this.lastTimestamp;
            // Skip if no actual scroll happened
            if (pixelDelta === 0) {
                return;
            }
            // Normalize scroll distance to document height (0-1 range)
            const normalizedDistance = Math.abs(pixelDelta) / this.documentHeight;
            // Record all scroll movements (no threshold - let pattern detection decide)
            if (deltaTime > 0) {
                const velocity = normalizedDistance / deltaTime; // normalized units per ms
                // Store as time series
                this.distanceSeries.push({ value: normalizedDistance, timestamp: now });
                this.velocitySeries.push({ value: velocity, timestamp: now });
                // Notify detector for confidence tracking
                this.notifyEvent(Math.min(1, normalizedDistance * 10));
                // Apply rolling window efficiently
                const cutoff = now - this.rollingWindowMs;
                while (this.distanceSeries.length > 0 && this.distanceSeries[0].timestamp < cutoff) {
                    this.distanceSeries.shift();
                    this.velocitySeries.shift();
                }
            }
            this.lastScrollY = scrollY;
            this.lastTimestamp = now;
        };
        // Listen on window for scroll events (document scrolling)
        window.addEventListener('scroll', this.listener, { passive: true });
    }
    stop() {
        if (!this.isActive)
            return;
        this.isActive = false;
        if (this.listener) {
            window.removeEventListener('scroll', this.listener);
            this.listener = null;
        }
    }
    reset() {
        this.distanceSeries = [];
        this.velocitySeries = [];
        this.lastScrollY = null;
        this.lastTimestamp = 0;
    }
    score() {
        if (this.distanceSeries.length < 2)
            return undefined;
        // Scroll-specific pattern detection
        return this.detectScrollPatterns();
    }
    /**
     * Scroll-specific pattern detection
     * Detects bot-like patterns: constant deltas, instant jumps, too smooth
     */
    detectScrollPatterns() {
        if (this.distanceSeries.length < 2)
            return undefined;
        let score = 0;
        let factors = 0;
        // 1. IDENTICAL DELTAS - Smoking gun for bots
        const distances = this.distanceSeries.map(p => p.value);
        if (distances.length >= 2) {
            // Check if all distances are identical (tolerance for floating point)
            const uniqueDistances = new Set(distances.map(d => Math.round(d * 1000))).size;
            if (uniqueDistances === 1) {
                // All scroll amounts identical = definite bot
                return 0.05;
            }
        }
        // 2. DISTANCE CONSISTENCY - Bots often scroll same amount repeatedly
        if (distances.length >= 2) {
            const stats = calculateStatistics(distances);
            // Real humans have high CV (>0.8) due to natural scroll acceleration/deceleration
            // Simple automation has lower CV even with varied amounts
            // Gaussian centered high with narrow width - be strict
            score += gaussian(stats.cv, 1.0, 0.4);
            factors++;
        }
        // 3. VELOCITY VARIATION - Humans have highly variable scroll speeds
        const velocities = this.velocitySeries.map(p => p.value);
        if (velocities.length >= 2) {
            const stats = calculateStatistics(velocities);
            // Human velocity is very chaotic (acceleration/deceleration)
            // Automation has more consistent velocity even with delays
            score += gaussian(stats.cv, 1.2, 0.5);
            factors++;
        }
        // 4. INSTANT JUMPS - Detect programmatic scrollTo
        const instantJumps = distances.filter(d => d > 0.1).length; // >10% of document in one event
        if (distances.length > 0) {
            const jumpRatio = instantJumps / distances.length;
            // Penalize high jump ratio (programmatic scrollTo)
            score += inverseSigmoid(jumpRatio, 0.3, 15);
            factors++;
        }
        return factors > 0 ? score / factors : undefined;
    }
    getDebugInfo() {
        return {
            eventCount: this.distanceSeries.length,
            rollingWindow: this.rollingWindowMs,
            isActive: this.isActive,
            distanceSeries: this.distanceSeries,
            velocitySeries: this.velocitySeries,
        };
    }
}
